/**
 * Error Handler Utility
 * Handles API errors gracefully and prevents showing sensitive information
 */

import toast from 'react-hot-toast';

/**
 * Safe error handler that displays user-friendly messages
 * @param {Error} error - The error object
 * @param {string} defaultMessage - Default message to show if no error message is available
 * @returns {string} The error message to display
 */
export const handleApiError = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  // If it's a 401 error, it's already handled by the API interceptor
  if (error?.response?.status === 401) {
    return null; // Don't show toast, API interceptor will handle it
  }

  // If it's a 403 error, it's already handled by the API interceptor
  if (error?.response?.status === 403) {
    return null; // Don't show toast, API interceptor will handle it
  }

  // Handle network errors
  if (!error?.response) {
    console.error('Network error:', error);
    return defaultMessage;
  }

  // Get error message from response
  const errorMessage =
    error.response?.data?.message ||
    error.response?.data?.error ||
    error.message ||
    defaultMessage;

  return errorMessage;
};

/**
 * Safely show error toast without sensitive information
 * @param {Error} error - The error object
 * @param {string} defaultMessage - Default message to show
 * @param {Object} toastOptions - Additional toast options
 */
export const showErrorToast = (error, defaultMessage = 'Something went wrong. Please try again.', toastOptions = {}) => {
  // Don't show toast for 401/403 errors (handled by API interceptor)
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return;
  }

  const message = handleApiError(error, defaultMessage);

  if (message) {
    toast.error(message, {
      duration: 4000,
      ...toastOptions,
    });
  }
};

/**
 * Check if error is a token expiration error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a token expiration error
 */
export const isTokenExpired = (error) => {
  return error?.response?.status === 401;
};

/**
 * Check if error is a permission error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a permission error
 */
export const isPermissionError = (error) => {
  return error?.response?.status === 403;
};

/**
 * Check if it's a network error
 * @param {Error} error - The error object
 * @returns {boolean} True if it's a network error
 */
export const isNetworkError = (error) => {
  return !error?.response;
};
